import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
} from '@chakra-ui/react';
import { MdExpandMore } from 'react-icons/md';

/**
 * App-wide dropdown — scrollable panel (not native OS select).
 * Works inside modals via Portal + high z-index.
 * Searchable mode disables Menu typeahead so the search field keeps focus.
 */
export function AppSelect({
  label,
  options = [],
  value,
  onChange,
  placeholder = 'Select…',
  isRequired = false,
  helperText,
  isSearchable = false,
  searchPlaceholder = 'Search…',
  h = '52px',
  labelFontSize,
  isDisabled = false,
  isInvalid = false,
  errorMessage,
}) {
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    if (!isSearchable) return options;
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const hay = `${o.label || ''} ${o.description || ''} ${o.value || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, isSearchable, query]);

  const stopMenuKeys = (e) => {
    // Prevent Chakra Menu typeahead / arrow focus from stealing the search input
    e.stopPropagation();
  };

  return (
    <FormControl isRequired={isRequired} isDisabled={isDisabled} isInvalid={isInvalid}>
      {label ? (
        <FormLabel fontSize={labelFontSize} mb={2}>
          {label}
        </FormLabel>
      ) : null}
      {isRequired ? (
        <input
          required
          tabIndex={-1}
          value={value || ''}
          onChange={() => {}}
          style={{
            position: 'absolute',
            opacity: 0,
            width: 1,
            height: 1,
            pointerEvents: 'none',
          }}
          aria-hidden
        />
      ) : null}

      <Menu
        isLazy
        matchWidth
        placement="bottom-start"
        strategy="fixed"
        gutter={4}
        autoSelect={false}
        closeOnSelect
        onOpen={() => {
          setQuery('');
          if (isSearchable) {
            // Focus after menu portal mounts
            requestAnimationFrame(() => searchRef.current?.focus());
          }
        }}
      >
        <MenuButton
          as={Button}
          type="button"
          variant="unstyled"
          w="100%"
          h={h}
          minH={h}
          maxH={h}
          px={4}
          py={0}
          fontWeight="400"
          fontSize="14px"
          lineHeight="1.2"
          bg="bg.input"
          border="1px solid"
          borderColor={isInvalid ? 'status.error' : 'border.subtle'}
          borderRadius="sm"
          color={selected ? 'text.primary' : 'text.placeholder'}
          _hover={{ bg: 'bg.input', borderColor: isInvalid ? 'status.error' : 'border.subtle' }}
          _active={{ bg: 'bg.input' }}
          _focus={{
            bg: 'bg.input',
            borderColor: isInvalid ? 'status.error' : 'utkarsh.amber',
            boxShadow: isInvalid
              ? '0 0 0 1px var(--chakra-colors-status-error)'
              : '0 0 0 1px #F9B101',
          }}
          isDisabled={isDisabled}
        >
          <Box
            as="span"
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            w="100%"
            gap={2}
          >
            <Text
              as="span"
              flex="1"
              minW={0}
              textAlign="left"
              noOfLines={1}
              fontWeight="400"
            >
              {selected ? selected.label : placeholder}
            </Text>
            <Box as="span" display="inline-flex" flexShrink={0} color="text.secondary">
              <MdExpandMore size={20} />
            </Box>
          </Box>
        </MenuButton>

        <Portal>
          <MenuList
            bg="bg.surface"
            borderColor="border.subtle"
            borderRadius="md"
            py={isSearchable ? 0 : 1}
            maxH="280px"
            overflowY="auto"
            overflowX="hidden"
            zIndex={2000}
            boxShadow="lg"
            minW="var(--chakra-sizes-3xs)"
            // Don't auto-focus first MenuItem (steals search focus)
            rootProps={isSearchable ? { tabIndex: -1 } : undefined}
            sx={{
              overscrollBehavior: 'contain',
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '8px',
              },
            }}
          >
            {isSearchable ? (
              <Box
                position="sticky"
                top={0}
                zIndex={2}
                bg="bg.surface"
                px={3}
                pt={3}
                pb={2}
                borderBottomWidth="1px"
                borderColor="border.subtle"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onKeyDown={stopMenuKeys}
                onKeyUp={stopMenuKeys}
              >
                <Input
                  ref={searchRef}
                  h="40px"
                  fontSize="14px"
                  placeholder={searchPlaceholder}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={stopMenuKeys}
                  onKeyUp={stopMenuKeys}
                  onBlur={(e) => {
                    // If focus jumped to a MenuItem via typeahead, pull it back
                    const next = e.relatedTarget;
                    if (next && next.getAttribute?.('role') === 'menuitem') {
                      requestAnimationFrame(() => searchRef.current?.focus());
                    }
                  }}
                  bg="bg.input"
                  autoComplete="off"
                />
              </Box>
            ) : null}

            {filtered.length === 0 ? (
              <Box px={4} py={3}>
                <Text fontSize="13px" color="text.secondary">
                  No matches
                </Text>
              </Box>
            ) : (
              filtered.map((o, idx) => {
                const active = String(o.value) === String(value ?? '');
                const pick = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange?.(o.value, o);
                };
                return (
                  <MenuItem
                    key={`opt-${String(o.value)}-${o.label}`}
                    // Omit typeahead value when searchable — prevents focus steal on keypress
                    value={isSearchable ? undefined : o.value === '' || o.value == null ? `__empty_${idx}` : String(o.value)}
                    closeOnSelect
                    tabIndex={isSearchable ? -1 : undefined}
                    onMouseDown={pick}
                    onClick={pick}
                    bg={active ? 'rgba(249, 177, 1, 0.16)' : 'transparent'}
                    color="text.primary"
                    fontSize="14px"
                    py={2.5}
                    px={4}
                    _hover={{ bg: 'rgba(249, 177, 1, 0.22)' }}
                    _focus={{ bg: 'rgba(249, 177, 1, 0.22)' }}
                    _active={{ bg: 'rgba(249, 177, 1, 0.28)' }}
                  >
                    <Box w="100%" pointerEvents="none">
                      <Text fontWeight={active ? '600' : '500'} noOfLines={1}>
                        {o.label}
                      </Text>
                      {o.description ? (
                        <Text fontSize="12px" color="text.secondary" noOfLines={1}>
                          {o.description}
                        </Text>
                      ) : null}
                    </Box>
                  </MenuItem>
                );
              })
            )}
          </MenuList>
        </Portal>
      </Menu>

      {isInvalid && errorMessage ? (
        <FormErrorMessage>{errorMessage}</FormErrorMessage>
      ) : helperText ? (
        <FormHelperText color="text.secondary">{helperText}</FormHelperText>
      ) : null}
    </FormControl>
  );
}
